import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useCustomer } from "@/lib/customer-context";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2, KeyRound, ShieldCheck } from "lucide-react";

interface SetPinModalProps {
  open: boolean;
  /** When true, the customer cannot dismiss the modal — used for forced first-time setup. */
  required?: boolean;
  /** Existing-PIN flow: when true, prompt for current PIN first. */
  changeMode?: boolean;
  onClose?: () => void;
}

export function SetPinModal({ open, required = false, changeMode = false, onClose }: SetPinModalProps) {
  const { toast } = useToast();
  const { refreshCustomer } = useCustomer();
  const [currentPin, setCurrentPin] = useState("");
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");

  const mutation = useMutation({
    mutationFn: async () => {
      const body: any = { pin };
      if (changeMode) body.currentPin = currentPin;
      const res = await apiRequest("POST", "/api/customer/auth/set-pin", body);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to set PIN");
      }
      return res.json();
    },
    onSuccess: async () => {
      toast({ title: changeMode ? "PIN updated" : "PIN set", description: "Your portal PIN is active." });
      await refreshCustomer();
      setCurrentPin("");
      setPin("");
      setConfirmPin("");
      onClose?.();
    },
    onError: (err: Error) => {
      toast({ title: "Failed", description: err.message, variant: "destructive" });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(pin)) {
      toast({ title: "Invalid PIN", description: "PIN must be exactly 6 digits", variant: "destructive" });
      return;
    }
    if (pin !== confirmPin) {
      toast({ title: "PINs don't match", description: "Please re-enter the same PIN twice.", variant: "destructive" });
      return;
    }
    if (changeMode && !/^\d{6}$/.test(currentPin)) {
      toast({ title: "Current PIN required", description: "Enter your existing 6-digit PIN.", variant: "destructive" });
      return;
    }
    mutation.mutate();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (required) return; // can't dismiss
        if (!o) onClose?.();
      }}
    >
      <DialogContent
        className="sm:max-w-md"
        onInteractOutside={required ? (e) => e.preventDefault() : undefined}
        onEscapeKeyDown={required ? (e) => e.preventDefault() : undefined}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            {changeMode ? "Change Your PIN" : "Set Up Your PIN"}
          </DialogTitle>
          <DialogDescription>
            {changeMode
              ? "Enter your current PIN, then choose a new 6-digit PIN."
              : "Choose a 6-digit PIN. You'll enter it after the SMS code on every sign-in."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {changeMode && (
            <div className="space-y-2">
              <Label htmlFor="current-pin">Current PIN</Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="current-pin"
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  value={currentPin}
                  onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, ""))}
                  className="pl-10 text-center tracking-widest"
                  placeholder="••••••"
                  autoFocus
                />
              </div>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="new-pin">New PIN</Label>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="new-pin"
                type="password"
                inputMode="numeric"
                maxLength={6}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                className="pl-10 text-center tracking-widest"
                placeholder="••••••"
                autoFocus={!changeMode}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-pin">Confirm PIN</Label>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="confirm-pin"
                type="password"
                inputMode="numeric"
                maxLength={6}
                value={confirmPin}
                onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))}
                className="pl-10 text-center tracking-widest"
                placeholder="••••••"
              />
            </div>
          </div>
          <Button type="submit" className="w-full" disabled={mutation.isPending}>
            {mutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {changeMode ? "Update PIN" : "Set PIN"}
          </Button>
          {required && (
            <p className="text-center text-xs text-muted-foreground">
              Setting a PIN is required to use the portal.
            </p>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
